/**
 * PortalShell — sidebar + topbar + outlet.
 *
 * Sidebar items are driven by the user's role permissions (Phase 7):
 *   GET /api/permissions/me  → { role, is_admin, modules: [keys] }
 * Each sidebar entry is gated on a module_key. Admins see everything.
 *
 * For client-tenant users, the bottom of the sidebar shows the client logo
 * pulled from /api/me/branding.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../theme/ThemeProvider';
import { permissionsApi } from '../api/client';
import { Softphone } from '../components/Softphone';

// Top-level sidebar entries. Each gated on one or more module_keys
// (ANY match grants visibility — sub-pages enforce their own gates).
//
// 2026-07-15 pm: items may declare `children` — an ordered list of
// sub-items that render as a hover flyout to the right of the sidebar.
// Every child is independently gated on its own `modules` list. Admin
// and QA Evaluations use this pattern.
const TOP_NAV = [
  { to: '/softphone',  icon: '🎙️', label: 'Softphone',  modules: ['softphone'] },
  { to: '/acd',        icon: '👥', label: 'ACD',        modules: ['acd_floor', 'acd_queues', 'acd_agent_state', 'acd_personal', 'acd_supervisor', 'acd_trunks'] },
  {
    to: '/admin',
    icon: '🛠️',
    label: 'Admin',
    modules: [
      'admin_agents','admin_queues','admin_announcements','admin_recording',
      'admin_dids','admin_dialing_profiles','admin_aux_codes','admin_qa_forms','admin_failover',
    ],
    children: [
      { to: '/admin/agents',            label: 'Agents',                   modules: ['admin_agents']            },
      { to: '/admin/queues',            label: 'Queues',                   modules: ['admin_queues']            },
      { to: '/admin/announcements',     label: 'Announcements',            modules: ['admin_announcements']     },
      { to: '/admin/dids',              label: 'DIDs / TFNs',              modules: ['admin_dids']              },
      { to: '/admin/dialing-profiles',  label: 'Outbound Dialing Profiles',modules: ['admin_dialing_profiles']  },
      { to: '/admin/aux-codes',         label: 'AUX Codes',                modules: ['admin_aux_codes']         },
      { to: '/admin/qa-forms',          label: 'QA Forms',                 modules: ['admin_qa_forms']          },
      { to: '/admin/qa-assign',         label: 'QA - Assign',              modules: ['admin_qa_forms']          },
      { to: '/admin/failover',          label: 'Cluster Failover',         modules: ['admin_failover']          },
      { to: '/admin/recording',         label: 'Recording',                modules: ['admin_recording']         },
      { to: '/admin/clusters',          label: 'Clusters',                 modules: ['admin_recording']         },
    ],
  },
  { to: '/recordings', icon: '📼', label: 'Recordings', modules: ['recordings_browser'] },
  {
    // Combined QA menu — the three QA modules live under this one
    // sidebar entry. Fernando's ask (2026-07-15 pm): one "QA Evaluations"
    // parent with a hover flyout showing the three children.
    to: '/qa',
    icon: '✅',
    label: 'QA Evaluations',
    modules: ['qa_evaluations','qa_manager','qa_insights'],
    children: [
      { to: '/qa/my-assignments', label: 'My QA Assignments', modules: ['qa_evaluations'] },
      { to: '/qa-manager',        label: 'QA Manager',        modules: ['qa_manager']     },
      { to: '/qa/insights',       label: 'QA Insights',       modules: ['qa_insights']    },
    ],
  },
  { to: '/reports',    icon: '📊', label: 'Reports',    modules: ['reports'] },
  { to: '/settings',   icon: '⚙️',  label: 'Settings',   modules: ['settings_permissions', 'settings_users', 'settings_clients'] },
  { to: '/account',    icon: '👤', label: 'Account',    modules: ['account'] },
];

export default function PortalShell() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: perms } = useQuery({
    queryKey: ['permissions', 'me'],
    queryFn: () => permissionsApi.me(),
    staleTime: 60_000,
  });
  const { data: branding } = useQuery({
    queryKey: ['branding', 'me'],
    queryFn: () => permissionsApi.branding(),
    staleTime: 60_000,
  });

  const allowedModules = useMemo(() => {
    if (!perms) return new Set();
    if (perms.is_admin) return new Set([
      'softphone','acd_floor','acd_queues','acd_agent_state','acd_personal','acd_supervisor','acd_trunks',
      'admin_agents','admin_queues','admin_announcements','admin_recording',
      'admin_dids','admin_dialing_profiles','admin_aux_codes','admin_qa_forms','admin_failover',
      'recordings_browser','reports','settings_permissions','settings_users','settings_clients','account',
      'qa_evaluations','qa_manager','qa_insights','qa_reports',
    ]);
    return new Set(perms.modules || []);
  }, [perms]);

  // Filter TOP_NAV to items the user can see, and (for parents with
  // children) filter each child list to just the child items the user
  // can access.
  const items = useMemo(() => {
    return TOP_NAV
      .filter((i) => i.modules.some((m) => allowedModules.has(m)))
      .map((i) => {
        if (!i.children) return i;
        const kids = i.children.filter((c) => c.modules.some((m) => allowedModules.has(m)));
        return { ...i, children: kids };
      })
      .filter((i) => !i.children || i.children.length > 0);
  }, [allowedModules]);

  const onSignOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-surface-base dark:bg-surface-dark-base text-surface-text dark:text-surface-dark-text">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel flex flex-col">
        <div className="px-4 py-4 border-b border-surface-border dark:border-surface-dark-border flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-continuum-500 text-white flex items-center justify-center text-sm font-semibold shadow-thin">
            CX
          </div>
          <span className="font-semibold tracking-tight">ContinuumCX</span>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto">
          {items.map((item) => (
            item.children
              ? <SidebarGroup key={item.to} item={item} />
              : <SidebarLink key={item.to} item={item} />
          ))}
        </nav>

        {/* Client branding footer */}
        {branding && !branding.is_continuum_employee && (
          <div className="border-t border-surface-border dark:border-surface-dark-border px-3 py-3 flex flex-col items-center gap-2">
            {branding.has_logo ? (
              <img
                src={branding.logo_url}
                alt={branding.client_name || 'Client logo'}
                className="max-h-12 w-auto object-contain"
              />
            ) : null}
            <div className="text-xs text-surface-muted dark:text-surface-dark-muted text-center">
              {branding.client_name}
            </div>
          </div>
        )}

        <div className="border-t border-surface-border dark:border-surface-dark-border px-3 py-3 text-xs text-surface-muted dark:text-surface-dark-muted">
          <div className="truncate" title={user?.email}>{user?.email}</div>
          <div className="capitalize">{user?.role}</div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 px-4 border-b border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel flex items-center justify-end gap-2">
          <button
            onClick={() => navigate('/help')}
            title="Browse all help articles"
            className="px-2 py-1.5 rounded-thin hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle text-sm"
          >
            ❓ Help
          </button>
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="px-2 py-1.5 rounded-thin hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle text-sm"
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <button
            onClick={onSignOut}
            className="px-3 py-1.5 rounded-thin border border-surface-border dark:border-surface-dark-border text-sm hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle"
          >
            Sign out
          </button>
        </header>

        <main className="flex-1 overflow-auto relative">
          {/* Persistent softphone — stays mounted across route changes so
              JsSIP UA + active calls survive navigation. We render the
              softphone first and hide it when off /softphone; the active
              route renders on top. */}
          {(() => {
            // Modules the current user can access. If they can't see
            // softphone (e.g. viewer), don't mount it at all.
            const allowedSet = perms?.is_admin
              ? new Set(['softphone'])
              : new Set(perms?.modules || []);
            if (!allowedSet.has('softphone')) {
              return <Outlet />;
            }
            const onSoftphone = location.pathname.startsWith('/softphone');
            return (
              <>
                <div
                  className={onSoftphone ? 'h-full' : 'hidden'}
                  aria-hidden={!onSoftphone}
                >
                  <Softphone onLogin={() => {}} onStatusChange={() => {}} />
                </div>
                {!onSoftphone && (
                  <div className="h-full">
                    <Outlet />
                  </div>
                )}
              </>
            );
          })()}
        </main>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// Sidebar link + group helpers (2026-07-15 pm)
// ---------------------------------------------------------------------
// SidebarLink: plain top-level entry with no children.
// SidebarGroup: parent entry with a hover-flyout showing children to
// the right of the sidebar. On the sidebar itself we also expand the
// child list when the current route is inside the parent's tree, so
// the user can see where they are.

function SidebarLink({ item }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        'flex items-center gap-3 px-3 py-2 rounded-thin text-sm transition-colors ' +
        (isActive
          ? 'bg-continuum-50 dark:bg-continuum-900/30 text-continuum-700 dark:text-continuum-200 font-medium'
          : 'hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle text-surface-text dark:text-surface-dark-text')
      }
    >
      <span className="text-base leading-none">{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  );
}

function SidebarGroup({ item }) {
  const location = useLocation();

  // "Inside" the group = current path is the parent OR one of the
  // children. Used to compute the initial `open` state and to keep
  // the parent visually active when a child page is showing.
  const inside =
    location.pathname === item.to ||
    location.pathname.startsWith(item.to + '/') ||
    (item.children || []).some((c) =>
      location.pathname === c.to || location.pathname.startsWith(c.to + '/')
    );

  // 2026-07-15 pm fix: click-to-toggle. The group opens when the user
  // clicks the parent row (or when they navigate into any child from
  // elsewhere). Clicking the parent again collapses it. This is
  // independent of route — they can browse a child page with the
  // group collapsed and re-open the group to jump siblings.
  const [open, setOpen] = useState(inside);

  // If the route changes into the group from outside, auto-open so
  // the sibling list is visible. Doesn't force-close on nav-out.
  useEffect(() => {
    if (inside) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={
          'w-full flex items-center gap-3 px-3 py-2 rounded-thin text-sm transition-colors text-left ' +
          (inside
            ? 'bg-continuum-50 dark:bg-continuum-900/30 text-continuum-700 dark:text-continuum-200 font-medium'
            : 'hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle text-surface-text dark:text-surface-dark-text')
        }
      >
        <span className="text-base leading-none">{item.icon}</span>
        <span className="flex-1">{item.label}</span>
        <span
          className={'text-[10px] opacity-60 select-none transition-transform ' + (open ? 'rotate-90' : '')}
          aria-hidden
        >▸</span>
      </button>

      {/* Inline expanded child list — shown when the group is open. */}
      {open && (
        <div className="mt-0.5 mb-1 pl-9 pr-1 flex flex-col gap-0.5">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              className={({ isActive }) =>
                'block px-2 py-1 rounded-thin text-[13px] transition-colors ' +
                (isActive
                  ? 'bg-continuum-100 dark:bg-continuum-900/50 text-continuum-800 dark:text-continuum-100 font-medium'
                  : 'text-surface-muted dark:text-surface-dark-muted hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle hover:text-surface-text dark:hover:text-surface-dark-text')
              }
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}

      {/* Hover flyout — shown only when the group is COLLAPSED. Lets
          a user peek at children without expanding. Once open, the
          inline list above is authoritative. */}
      {!open && (
        <div
          className="hidden group-hover:block absolute left-full top-0 z-40 min-w-[220px]
                     ml-1 rounded-thin border border-surface-border dark:border-surface-dark-border
                     bg-surface-panel dark:bg-surface-dark-panel shadow-lg
                     transition-opacity"
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-surface-muted dark:text-surface-dark-muted border-b border-surface-border dark:border-surface-dark-border">
            {item.label}
          </div>
          <div className="py-1">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                className={({ isActive }) =>
                  'block px-3 py-1.5 text-sm transition-colors ' +
                  (isActive
                    ? 'bg-continuum-50 dark:bg-continuum-900/30 text-continuum-700 dark:text-continuum-200 font-medium'
                    : 'text-surface-text dark:text-surface-dark-text hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
